import type * as Y from 'yjs'
import { computeChangedRegion } from '../../lib/diff'
import { parseSections } from '../../lib/sections'

/** What `syncYText` did. `blocked` means the caller must re-sync from the CRDT — the local
 *  edit was dropped and the editor now holds text the Y.Text does not. */
export type SyncOutcome = 'unchanged' | 'applied' | 'blocked'

/**
 * Does the window [start, start+removedLength) reach into a § somebody else holds?
 *
 * `locked` carries outline numbers, `''` meaning the whole part. Ranges are resolved against
 * `markdown` — the CURRENT Y.Text — because that is the coordinate system `computeChangedRegion`
 * produced `start` in, and the same bytes the backend numbered when it keyed the lock.
 *
 * `offset` is what puts both sides in the same numbering space: lock numbers come from the
 * server and are ARTEFACT-wide, so numbering this part from 1 would match none of them in
 * part 2 onwards and the guard would silently stop blocking.
 *
 * A pure insertion is an empty window, so it is tested for containment rather than overlap, and
 * the § bounds are treated as closed. Over-blocking at a boundary costs one keystroke; under-
 * blocking corrupts someone else's paragraph.
 */
function intersectsLocked(
  markdown: string,
  start: number,
  removedLength: number,
  locked: string[],
  offset: number,
): boolean {
  if (locked.includes('')) return true
  const sections = parseSections(markdown, offset)
  const to = start + removedLength
  for (const s of sections) {
    if (!locked.includes(s.number)) continue
    if (removedLength === 0 ? start >= s.start && start <= s.end : start < s.end && to > s.start) return true
  }
  return false
}

/**
 * Apply `next` onto a Y.Text with a minimal common-prefix/suffix diff (no diff
 * library — `diff` is explicitly banned). Produces small CRDT ops so concurrent
 * remote edits outside the changed range survive. `origin` tags the transaction
 * so the writer's own observer can ignore it.
 *
 * `locked` closes a read-only bypass at this layer. SectionNumbers.filterTransaction vets
 * ProseMirror STEP ranges, but the window computed here is routinely much wider than the step
 * that triggered it: the browser's canonical markdown differs from hand-authored agent markdown
 * (prosemirror-markdown escapes `*`, `[`, `]`, … in every text node), so the first keystroke
 * anywhere flushes all the accumulated drift as one span reaching to EOF. A keystroke at PM
 * position 204, well outside §1.1, was measured rewriting 121 bytes INSIDE locked §1.1.
 *
 * When that happens the write is refused outright rather than clamped: a clamped window is not
 * the same edit, and applying half of one is how text corrupts silently. The caller re-syncs from
 * the CRDT, which costs the local keystroke — correct, since it landed in a § the member does not
 * hold and filterTransaction should have rejected it in the first place.
 */
export function syncYText(
  ytext: Y.Text,
  next: string,
  origin: object,
  locked: string[] = [],
  offset = 0,
): SyncOutcome {
  const cur = ytext.toString()
  if (cur === next) return 'unchanged'
  const { start, removed, added } = computeChangedRegion(cur, next)
  if (locked.length > 0 && intersectsLocked(cur, start, removed.length, locked, offset)) {
    return 'blocked'
  }
  ytext.doc?.transact(() => {
    if (removed.length > 0) ytext.delete(start, removed.length)
    if (added.length > 0) ytext.insert(start, added)
  }, origin)
  return 'applied'
}
